import { useCallback, useEffect, useMemo } from 'react';
import { z } from 'zod';
import { BUSINESS_INFORMATION_ROUTE, StepRoute } from '../../../../../../../i18n/pathnames/licenses';
import { useErrorValidator } from '../../../../../../../src/hooks/useErrorValidator';
import { useLicenseStore } from '../../../licenseStore';
import { LicenseCode } from '../../../types/licenses';

export type LicenseBusinessConfig<Schema extends z.ZodTypeAny> = {
  schema: Schema;
  defaults: () => Partial<z.infer<Schema>>;
  informationSlug?: StepRoute;
};

/**
 * Generic business information hook for license flows.
 * Centralizes:
 *  - Bootstrapping & switching current license
 *  - Resetting license cost on mount (to force recalculation)
 *  - Validation via Zod schema
 *  - Disabled states for inputs & calculate actions
 *  - Type-safe field updates
 */

export function useLicenseBusiness<Schema extends z.ZodTypeAny>(
  licenseCode: LicenseCode,
  config: LicenseBusinessConfig<Schema>,
): UseLicenseBusinessReturn<z.infer<Schema>> {
  const schema = config.schema;
  const defaultsFn = config.defaults;
  const informationSlug = config.informationSlug ?? BUSINESS_INFORMATION_ROUTE;

  type Business = z.infer<Schema>;

  const {
    updateLicenseForm,
    businessInformation,
    resetLicensePlan,
    showErrors,
    setShowErrors,
    licensePlan,
    setStepValidity,
  } = useLicenseStore(s => ({
    updateLicenseForm: s.updateLicenseForm,
    businessInformation: s.businessInformation as Business | undefined,
    resetLicensePlan: s.resetLicensePlan,
    showErrors: s.showErrors,
    setShowErrors: s.setShowErrors,
    licensePlan: s.licensePlan,
    setStepValidity: s.setStepValidity,
  }));

  // `showErrors` is persisted across the multi-step flow. If a previous step set it to true
  // (e.g. user clicked Next and validation failed), we don't want a newly entered step
  // to immediately show errors before the user interacts.
  useEffect(() => {
    setShowErrors(false);
  }, [setShowErrors]);

  useEffect(() => {
    // Enforce recalculation after remount / navigation.
    resetLicensePlan();
  }, [resetLicensePlan, licenseCode]);

  const defaults = useMemo(() => defaultsFn(), [defaultsFn]);

  const values: Business = useMemo(() => {
    return {
      ...defaults,
      ...(businessInformation ?? {}),
    } as Business;
  }, [businessInformation, defaults]);

  const { getError, isValid, errors } = useErrorValidator(values, schema ?? z.any());

  const effectiveIsValid = !!schema && isValid;

  useEffect(() => {
    setStepValidity(informationSlug, effectiveIsValid);
  }, [effectiveIsValid, informationSlug, setStepValidity]);

  const setField = useCallback(
    <K extends keyof Business & string>(key: K, value: Business[K] | undefined) => {
      const normalizedValue = typeof value === 'number' && Number.isNaN(value) ? undefined : value;
      updateLicenseForm({ [key]: normalizedValue } as never);
    },
    [updateLicenseForm],
  );

  // Disable inputs when license cost already computed.
  const costCalculated = !!licensePlan;

  const result = {
    values,
    setField,
    getError,
    errors,
    isValid: effectiveIsValid,
    licensePlan,
    costCalculated,
    showErrors,
  };
  return result as UseLicenseBusinessReturn<Business>;
}

export type UseLicenseBusinessReturn<T extends Record<string, unknown>> = {
  values: T;
  setField: <K extends keyof T & string>(key: K, value: T[K] | undefined) => void;
  getError: (key: keyof T) => string | undefined;
  errors: Record<string, string[]>;
  isValid: boolean;
  licenseCost?: number;
  costCalculated: boolean;
  showErrors: boolean;
};
